import { Colors, Pagination, Text } from '@amelio/ui-library';
import { useEffect, useMemo, useState } from 'react';

import { EmployeeRow } from './EmployeeRow';
import { EmployeeTableHeader, type SortColumn, type SortDirection } from './EmployeeTableHeader';
import { TableToolbar } from './TableToolbar';
import { TABLE_PAGE_SIZE } from '../calibrationConstants';
import { TABLE_CONTAINER } from '../calibrationStyles';

import type { JobTitleLabels } from '../utils/jobTitleLabels';
import type {
    CalibrationEmployee,
    CalibrationManager,
    CalibrationReport,
} from '@/types/calibration';

import { useAmelioTranslations } from '@/hooks';
import { getFullName } from '@/utils/user';

export interface EmployeeCalibrationTableProps {
    report: CalibrationReport;
    cycleEvaluationId: string;
    language: string;
    jobTitleLabels: JobTitleLabels;
}

interface FlatRow {
    employee: CalibrationEmployee;
    managerName: string;
}

const currentCalibratedScore = (employee: CalibrationEmployee): number =>
    employee.adjustment?.calibratedScore ?? employee.score;

const sortValue = (row: FlatRow, column: SortColumn): string | number => {
    switch (column) {
        case 'person':
            return getFullName(row.employee).toLocaleLowerCase();
        case 'manager':
            return row.managerName.toLocaleLowerCase();
        case 'score':
            return row.employee.score;
        case 'calibratedScore':
            return currentCalibratedScore(row.employee);
    }
};

const flattenManagers = (managers: CalibrationManager[]): FlatRow[] =>
    managers.flatMap((manager) =>
        manager.employees.map((employee) => ({ employee, managerName: getFullName(manager) })),
    );

/** The employee grid: every evaluated employee, searchable, sortable by column, paginated. */
export const EmployeeCalibrationTable = ({
    report,
    cycleEvaluationId,
    language,
    jobTitleLabels,
}: EmployeeCalibrationTableProps) => {
    const { t } = useAmelioTranslations();

    const [searchQuery, setSearchQuery] = useState('');
    const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [currentPage, setCurrentPage] = useState(1);

    const allRows = useMemo(() => flattenManagers(report.managers), [report.managers]);

    const filteredRows = useMemo(() => {
        const query = searchQuery.trim().toLocaleLowerCase();
        if (!query) {
            return allRows;
        }

        return allRows.filter((row) =>
            getFullName(row.employee).toLocaleLowerCase().includes(query),
        );
    }, [allRows, searchQuery]);

    const sortedRows = useMemo(() => {
        if (!sortColumn) {
            return filteredRows;
        }

        const factor = sortDirection === 'asc' ? 1 : -1;
        return [...filteredRows].sort((a, b) => {
            const valueA = sortValue(a, sortColumn);
            const valueB = sortValue(b, sortColumn);

            if (typeof valueA === 'number' && typeof valueB === 'number') {
                return (valueA - valueB) * factor;
            }

            return String(valueA).localeCompare(String(valueB)) * factor;
        });
    }, [filteredRows, sortColumn, sortDirection]);

    // Resets on a cycle switch or a new search, not on every report refetch: an autosave PUT
    // invalidates and refetches the very same cycle/filter scope, and must not kick the user back
    // to page 1 while they are still editing a later page. A filter change that shrinks the row
    // count is instead handled by clamping `safePage` below.
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, cycleEvaluationId]);

    const totalPages = Math.max(1, Math.ceil(sortedRows.length / TABLE_PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const pageRows = sortedRows.slice((safePage - 1) * TABLE_PAGE_SIZE, safePage * TABLE_PAGE_SIZE);

    const handleSort = (column: SortColumn) => {
        if (column === sortColumn) {
            setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setSortColumn(column);
        setSortDirection('asc');
    };

    // The card above only renders this table for a report that has employees, so an empty table
    // is always the search's doing.
    if (pageRows.length === 0) {
        return (
            <div className={'flex flex-col gap-4'}>
                <TableToolbar
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                />

                <Text color={Colors.BASE_400}>{t('calibration.table.empty')}</Text>
            </div>
        );
    }

    return (
        <div className={'flex flex-col gap-4'}>
            <TableToolbar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
            />

            {/* Keyboard-scrollable (`tabIndex`), hence a named region: a focus stop that
                announces nothing leaves a keyboard or screen-reader user with no idea what they
                just landed on. */}
            <div
                className={TABLE_CONTAINER}
                tabIndex={0}
                role={'region'}
                aria-label={t('calibration.table.regionLabel')}
            >
                <table className={'w-full text-sm'}>
                    <EmployeeTableHeader
                        activeColumn={sortColumn}
                        direction={sortDirection}
                        onSort={handleSort}
                    />

                    <tbody>
                        {pageRows.map(({ employee, managerName }) => (
                            <EmployeeRow
                                key={employee.employeeId}
                                employee={employee}
                                managerName={managerName}
                                jobTitle={
                                    employee.jobTitleId
                                        ? (jobTitleLabels[employee.jobTitleId] ?? '')
                                        : ''
                                }
                                levels={report.performanceLevels}
                                cycleEvaluationId={cycleEvaluationId}
                                language={language}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <Pagination
                    currentPage={safePage}
                    totalPages={totalPages}
                    totalCount={sortedRows.length}
                    pageSize={TABLE_PAGE_SIZE}
                    onPageChange={setCurrentPage}
                    dataTestId={'calibration-table-pagination'}
                />
            )}
        </div>
    );
};
